import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
  Fade,
  CircularProgress,
  Pagination,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Search,
  Clear,
  PlayArrow,
  BookmarkBorder,
  ArrowBack,
  Favorite,
  FavoriteBorder,
  PlayCircleFilled,
  AudioFile,
  VolumeUp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchAudio, filterAudio, getFilterOptions, fetchAllMetadata } from '../api/audio';
import { chakraColors } from '../theme/theme';
import { useTheme } from '@mui/material/styles';
import NavBar from '../components/NavBar';
import Modal from '@mui/material/Modal';
import AudioModal from '../components/AudioModal';
import { useAuth } from '../contexts/AuthContext';

const getInitialFavourites = () => {
  try {
    return JSON.parse(localStorage.getItem('favouriteAudio')) || [];
  } catch {
    return [];
  }
};

// Helper function to parse comma-separated values or return array as-is
const parseCommaSeparated = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }
  return [];
};

// Helper function to capitalize chakra names to match chakraColors keys
const capitalizeChakra = (chakra) => {
  if (!chakra) return '';
  
  // Handle special cases
  const specialCases = {
    'thirdeye': 'ThirdEye',
    'third eye': 'ThirdEye',
    'thirdEye': 'ThirdEye',
    'solarplexus': 'Solar',
    'solar plexus': 'Solar',
    'solarPlexus': 'Solar'
  };
  
  const lowerChakra = chakra.toLowerCase().replace(/\s+/g, '');
  if (specialCases[lowerChakra]) {
    return specialCases[lowerChakra];
  }
  
  // Capitalize first letter for normal chakras
  return chakra.charAt(0).toUpperCase() + chakra.slice(1).toLowerCase();
};

const AudioCatalog = () => {
  const [audio, setAudio] = useState([]);
  const [filteredAudio, setFilteredAudio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [chakraFilter, setChakraFilter] = useState('');
  const [emotionFilter, setEmotionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ chakras: [], emotions: [], categories: [] });
  const [favourites, setFavourites] = useState(getInitialFavourites());
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAudio, setModalAudio] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  useEffect(() => {
    const loadAudio = async () => {
      try {
        const [metadata, allAudio] = await Promise.all([
          fetchAllMetadata(),
          fetchAudio()
        ]);
        setAudio(allAudio);
        setFilteredAudio(allAudio);
        
        console.log('Raw metadata:', metadata);
        console.log('metadata.chakras:', metadata.chakras);
        console.log('metadata.emotions:', metadata.emotions);
        console.log('metadata.videoCategories:', metadata.videoCategories);
        
        const fo = {
            chakras: Array.from(metadata.chakras.map(c => c.chakra) || []).sort(),
            emotions: Array.from(metadata.emotions.map(e => e.emotion) || []).sort(),
            categories: Array.from(metadata.videoCategories.map(c => c.category) || []).sort()
          }
        console.log('Processed filterOptions:', fo)
        setFilterOptions(fo);
      } catch (error) {
        console.error('Error loading audio:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAudio();
  }, []);

  useEffect(() => {
    // Apply filters when search term or filters change
    const applyFilters = async () => {
      setLoading(true);
      try {
        if (!searchTerm && !chakraFilter && !emotionFilter && !categoryFilter) {
          setFilteredAudio(audio);
        } else {
          const filtered = await filterAudio(audio, searchTerm, chakraFilter, emotionFilter, categoryFilter);
          setFilteredAudio(filtered);
        }
      } catch (error) {
        console.error('Error filtering audio:', error);
        // On error, show unfiltered audio
        setFilteredAudio(audio);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimeout = setTimeout(applyFilters, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, chakraFilter, emotionFilter, categoryFilter, audio]);

  useEffect(() => {
    localStorage.setItem('favouriteAudio', JSON.stringify(favourites));
  }, [favourites]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClearChakraFilter = () => {
    setChakraFilter('');
  };

  const handleClearEmotionFilter = () => {
    setEmotionFilter('');
  };

  const handleClearCategoryFilter = () => {
    setCategoryFilter('');
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setChakraFilter('');
    setEmotionFilter('');
    setCategoryFilter('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (chakraFilter) count++;
    if (emotionFilter) count++;
    if (categoryFilter) count++;
    return count;
  };

  const toggleFavourite = (id) => {
    setFavourites((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const openListenModal = (audio) => {
    setModalAudio(audio);
    setModalOpen(true);
  };
  
  const closeListenModal = () => {
    setModalOpen(false);
    setModalAudio(null);
  };

  const filtered = showFavouritesOnly
    ? filteredAudio.filter((a) => favourites.includes(a.id))
    : filteredAudio;

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageAudio = filtered.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, chakraFilter, emotionFilter, categoryFilter, showFavouritesOnly]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const AudioCard = ({ audio }) => {
    const isDark = theme.palette.mode === 'dark';
    const chakras = parseCommaSeparated(audio.chakras);
    const emotions = parseCommaSeparated(audio.emotions);
    const tags = parseCommaSeparated(audio.tags);
    
    // Use the first chakra for determining the glow color
    const primaryChakra = chakras[0] || 'root';
    const capitalizedChakra = capitalizeChakra(primaryChakra);
    const glowColor = chakraColors[capitalizedChakra] || chakraColors['Root'];
    
    return (
      <Fade in={true} timeout={500}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            border: `2px solid ${glowColor}30`,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${glowColor}60`,
              borderColor: `${glowColor}80`
            }
          }}
        >
          {/* Background Image */}
          {audio.backgroundImageUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${audio.backgroundImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: isDark ? 0.15 : 0.1,
                zIndex: 0
              }}
            />
          )}
          
          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? `linear-gradient(135deg, ${glowColor}22 0%, ${theme.palette.background.paper}CC 100%)`
                : `linear-gradient(135deg, ${glowColor}20 0%, ${theme.palette.background.paper}CC 100%)`,
              zIndex: 1
            }}
          />

          <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                {audio.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={() => toggleFavourite(audio.id)} size="small" color="error">
                  {favourites.includes(audio.id) ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" >{audio.authorName}</Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '4.8em',
                mt: 1
              }}
            >
              {audio.excerpt}
            </Typography>

            <Box flexGrow={1} />
            <Box display="flex" gap={1} mt={2} flexWrap="wrap">
              {/* Primary Chakra Chip */}
              {primaryChakra && (
                <Chip
                  label={capitalizeChakra(primaryChakra)}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    bgcolor: glowColor,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'default'
                  }}
                />
              )}
              
              {/* Primary Emotion */}
              {emotions[0] && (
                <Chip
                  label={emotions[0]}
                  size="small"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    borderColor: glowColor,
                    color: glowColor,
                    fontWeight: 500,
                    borderWidth: 2,
                    cursor: 'default'
                  }}
                />
              )}

              {tags && tags.length > 0 && (
                <Chip
                  label={tags[0]}
                  size="small"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    cursor: 'default'
                  }}
                />
              )}
            </Box>
          </CardContent>

          <CardActions sx={{ p: 3, pt: 0, flexShrink: 0, position: 'relative', zIndex: 2 }}>
            <Button
              variant="contained"
              startIcon={<VolumeUp />}
              size="small"
              sx={{ mr: 1, flex: 1 }}
              onClick={() => openListenModal(audio)}
            >
              Listen
            </Button>
          </CardActions>
        </Card>
      </Fade>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 0
      }}
    >
      <NavBar user={user} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box mb={4} display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Discover Your Perfect Audio
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Explore our curated collection of audio for meditation, healing, and growth
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant={showFavouritesOnly ? 'contained' : 'outlined'}
              color="error"
              startIcon={<Favorite />}
              onClick={() => setShowFavouritesOnly((v) => !v)}
              sx={{ minWidth: 180 }}
            >
              {showFavouritesOnly ? 'Showing Favourites' : 'Show Favourites Only'}
            </Button>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Box mb={4}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: '2fr 1fr 1fr 1fr 1fr',
                lg: '2fr 1fr 1fr 1fr 1fr'
              },
              gap: 2,
              alignItems: 'center',
              mb: 2
            }}
          >
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search audio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Chakra Filter */}
            <FormControl fullWidth>
              <InputLabel>Chakra</InputLabel>
              <Select
                value={chakraFilter}
                label="Chakra"
                onChange={(e) => setChakraFilter(e.target.value)}
              >
                <MenuItem value="">All Chakras</MenuItem>
                {filterOptions.chakras.map((chakra) => (
                  <MenuItem key={chakra} value={chakra}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: chakraColors[capitalizeChakra(chakra)] || '#666'
                        }}
                      />
                      {capitalizeChakra(chakra)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Emotion Filter */}
            <FormControl fullWidth>
              <InputLabel>Emotion</InputLabel>
              <Select
                value={emotionFilter}
                label="Emotion"
                onChange={(e) => setEmotionFilter(e.target.value)}
              >
                <MenuItem value="">All Emotions</MenuItem>
                {filterOptions.emotions.map((emotion) => (
                  <MenuItem key={emotion} value={emotion}>
                    {emotion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Category Filter */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {filterOptions.categories?.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {getActiveFiltersCount() > 0 && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearAllFilters}
                startIcon={<Clear />}
                sx={{ minHeight: 56 }}
              >
                Clear ({getActiveFiltersCount()})
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  onDelete={handleClearSearch}
                  onClick={handleClearSearch}
                  color="primary"
                />
              )}
              {chakraFilter && (
                <Chip
                  label={`Chakra: ${capitalizeChakra(chakraFilter)}`}
                  onDelete={handleClearChakraFilter}
                  onClick={handleClearChakraFilter}
                  sx={{
                    bgcolor: `${chakraColors[capitalizeChakra(chakraFilter)]}20`,
                    color: chakraColors[capitalizeChakra(chakraFilter)],
                    borderColor: chakraColors[capitalizeChakra(chakraFilter)]
                  }}
                />
              )}
              {emotionFilter && (
                <Chip
                  label={`Emotion: ${emotionFilter}`}
                  onDelete={handleClearEmotionFilter}
                  onClick={handleClearEmotionFilter}
                  color="secondary"
                />
              )}
              {categoryFilter && (
                <Chip
                  label={`Category: ${categoryFilter}`}
                  onDelete={handleClearCategoryFilter}
                  onClick={handleClearCategoryFilter}
                  color="info"
                />
              )}
            </Box>
          )}
        </Box>

        {/* Results */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Typography variant="h6" color="text.secondary">
            {loading ? 'Loading...' : `${filtered.length} audio track${filtered.length !== 1 ? 's' : ''} found`}
          </Typography>
          {!loading && filtered.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </Typography>
          )}
        </Box>

        {/* Audio Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No audio found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        ) : currentPageAudio.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No audio on this page
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try going to an earlier page or changing your page size
            </Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              sx={{ mt: 2 }}
              onClick={() => setCurrentPage(1)}
            >
              Go to First Page
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: 3,
              alignItems: 'stretch'
            }}
          >
            {currentPageAudio.map((audio) => (
              <AudioCard key={audio.id} audio={audio} />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box mt={4} display="flex" justifyContent="center">
            <Stack spacing={3} alignItems="center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'medium' : 'large'}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 1 : 2}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    color: theme.palette.text.primary,
                    '&.Mui-selected': {
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  },
                }}
              />
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                  Items per page:
                </Typography>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      '& .MuiSelect-select': {
                        paddingY: 1,
                        paddingX: 2,
                      },
                    }}
                  >
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={16}>16</MenuItem>
                    <MenuItem value={32}>32</MenuItem>
                    <MenuItem value={64}>64</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>
        )}
      </Container>
      
      {/* Listen Modal */}
      <AudioModal
        open={modalOpen}
        onClose={closeListenModal}
        title={modalAudio?.title || ''}
        audioUrl={modalAudio?.audioUrl || ''}
        content={modalAudio?.fullContent || ''}
        excerpt={modalAudio?.excerpt || ''}
      />
    </Box>
  );
};

export default AudioCatalog; 